export type LifePlace = {
  id: string;
  name: string;
  href: string;
  note?: string;
  categories: string[];
  categoryIds: string[];
  area?: string;
};

export type LifeContentSnapshot = {
  places: LifePlace[];
  updatedAt: string;
};

export function isLifePlace(value: unknown): value is LifePlace {
  if (!value || typeof value !== "object") return false;
  const place = value as Partial<LifePlace>;
  return typeof place.id === "string"
    && typeof place.name === "string"
    && typeof place.href === "string"
    && Array.isArray(place.categories)
    && place.categories.every((category) => typeof category === "string")
    && Array.isArray(place.categoryIds)
    && place.categoryIds.every((categoryId) => typeof categoryId === "string")
    && (place.note === undefined || typeof place.note === "string")
    && (place.area === undefined || typeof place.area === "string");
}

export function parseLifeContentSnapshot(value: unknown): LifeContentSnapshot | undefined {
  if (!value || typeof value !== "object") return undefined;
  const snapshot = value as Partial<LifeContentSnapshot>;
  if (!Array.isArray(snapshot.places) || !snapshot.places.every(isLifePlace)) return undefined;
  if (typeof snapshot.updatedAt !== "string" || !Number.isFinite(Date.parse(snapshot.updatedAt))) return undefined;
  return { places: snapshot.places, updatedAt: snapshot.updatedAt };
}

const LIFE_SHOP_PREFIX = "/life-in-dulan/shop/";
const NOTION_PAGE_ID_PATTERN = /^[a-f0-9]{32}$/i;

export function normalizeLifeNotionId(value: string): string | null {
  const normalized = value.replaceAll("-", "").trim().toLowerCase();
  return NOTION_PAGE_ID_PATTERN.test(normalized) ? normalized : null;
}

export function lifeShopPath(id: string): string | null {
  const normalized = normalizeLifeNotionId(id);
  return normalized ? `${LIFE_SHOP_PREFIX}${normalized}` : null;
}

export function lifeShopIdFromPath(path: string): string | null {
  if (!path.startsWith(LIFE_SHOP_PREFIX)) return null;
  return normalizeLifeNotionId(path.slice(LIFE_SHOP_PREFIX.length));
}

export function lifeNotionEmbedUrl(id: string): string | null {
  const normalized = normalizeLifeNotionId(id);
  return normalized ? `https://dulanstar.notion.site/ebd/${normalized}` : null;
}

export function lifeNotionPageUrl(id: string): string | null {
  const normalized = normalizeLifeNotionId(id);
  return normalized ? `https://dulanstar.notion.site/${normalized}` : null;
}

export type LifePlaceLink = Pick<LifePlace, "id" | "name" | "href" | "note">;

export type LifeGroup = {
  title: string;
  intro?: string;
  links: LifePlaceLink[];
};

export type LifeSection = {
  title: string;
  en: string;
  groups: LifeGroup[];
};

type GroupDefinition = Omit<LifeGroup, "links"> & {
  category?: string;
  matches?: (place: LifePlace) => boolean;
};

export const lifeCategoryIds = {
  breakfast: "0ef346a0-4d23-4366-85c3-811762598a86",
  food: "d92012b8-3682-4332-b6a8-8ab7dbe7c66a",
  coffeeAndSweets: "ebf3dbd3-ca9d-46c0-b259-8818fb44de92",
  drinksAndIce: "bd30491d-4aa0-4ade-9734-54175d634a5a",
  barsAndNightlife: "b8c848c2-98c9-48f6-a560-84bcc620ace8",
  marketsAndEvents: "9611d588-bfbd-4777-aeb6-a3e7ad7abffc",
  beachesAndSunrise: "9e0bcf1c-98b4-4fb1-9613-4f89ea4432b3",
  sightsAndWalks: "2b03cecb-084f-4c6e-bc2d-3d9f5b1d5289",
  artsAndIndoors: "6a6c23c0-042d-407a-9d9a-bf251c2791b0",
  shopsAndEssentials: "010a2b1c-0173-4ba8-b298-bbc453bd5943",
  yoga: "abba6dd0-6ee0-42eb-b8a1-f1ef87234bfa",
  massageAndWellness: "36285009-f7b5-45ae-a76a-1aae8627229b",
  surfing: "b3a53c2f-cf00-478a-91fd-899ff4248bc0",
  stargazingAndMoonlitSea: "20b157f8-bc4c-4a01-aba6-ebd3cae2cfae",
  workshops: "034da07c-3223-47d5-83d5-9d981adf7d7a",
  localExperiences: "23c41ffe-1d1f-433d-96df-44bed5dadc2c",
} as const;

const sectionDefinitions: Array<{
  title: string;
  en: string;
  groups: GroupDefinition[];
}> = [
  {
    title: "吃・喝",
    en: "Food & Drinks",
    groups: [
      {
        title: "晨間・Breakfast & Brunch",
        category: lifeCategoryIds.breakfast,
        intro: "*營業時間請確認各店家官方公告 · Please check each store’s official info for hours.",
      },
      { title: "食物・Food", category: lifeCategoryIds.food },
      { title: "咖啡甜點・Coffee & Sweets", category: lifeCategoryIds.coffeeAndSweets },
      { title: "飲料冰品・Drinks & Ice", category: lifeCategoryIds.drinksAndIce },
      { title: "酒吧夜生活・Bars & Nightlife", category: lifeCategoryIds.barsAndNightlife },
    ],
  },
  {
    title: "玩・樂",
    en: "Explore",
    groups: [
      { title: "市集活動・Markets & Events", category: lifeCategoryIds.marketsAndEvents },
      {
        title: "海邊日出・Beaches & Sunrise",
        category: lifeCategoryIds.beachesAndSunrise,
        intro: "*海邊與戶外活動有一定風險，請評估當日天候及自身狀況。",
      },
      {
        title: "景點散步・Sights & Walks",
        category: lifeCategoryIds.sightsAndWalks,
        intro: "*戶外活動有一定風險，請評估當日天候及自身狀況。",
      },
      { title: "藝文室內・Arts & Indoors", category: lifeCategoryIds.artsAndIndoors },
      { title: "採買生活・Shops & Essentials", category: lifeCategoryIds.shopsAndEssentials },
    ],
  },
  {
    title: "體・驗",
    en: "Experience",
    groups: [
      { title: "瑜珈・Yoga", category: lifeCategoryIds.yoga },
      { title: "按摩療癒・Massage & Wellness", category: lifeCategoryIds.massageAndWellness },
      { title: "衝浪・Surfing", category: lifeCategoryIds.surfing },
      {
        title: "觀星月光海・Stargazing & Moonlit Sea",
        category: lifeCategoryIds.stargazingAndMoonlitSea,
        intro: "滿月前後、天空晴朗且光害較少時，更容易遇見海面上的月光與滿天星星。",
      },
      { title: "手作體驗・Workshops", category: lifeCategoryIds.workshops },
      { title: "在地體驗・Local Experiences", category: lifeCategoryIds.localExperiences },
    ],
  },
];

function matchesGroup(place: LifePlace, group: GroupDefinition): boolean {
  if (group.matches) return group.matches(place);
  return Boolean(group.category && place.categoryIds.includes(group.category));
}

export function organizeLifePlaces(places: LifePlace[]): LifeSection[] {
  const groupDefinitions = sectionDefinitions.flatMap((section) => section.groups);

  const sections = sectionDefinitions
    .map((section) => ({
      title: section.title,
      en: section.en,
      groups: section.groups
        .map((group) => {
          const links = places
            .filter((place) => matchesGroup(place, group))
            .map((place) => ({
              id: place.id,
              name: place.name,
              href: place.href,
              ...(place.note ? { note: place.note } : {}),
            }));
          return { title: group.title, ...(group.intro ? { intro: group.intro } : {}), links };
        })
        .filter((group) => group.links.length > 0),
    }))
    .filter((section) => section.groups.length > 0);

  const remaining = places
    .filter((place) => !groupDefinitions.some((group) => matchesGroup(place, group)))
    .map((place) => ({
      id: place.id,
      name: place.name,
      href: place.href,
      ...(place.note ? { note: place.note } : {}),
    }));
  if (remaining.length > 0) {
    let explore = sections.find((section) => section.en === "Explore");
    if (!explore) {
      explore = { title: "玩・樂", en: "Explore", groups: [] };
      sections.push(explore);
    }
    explore.groups.push({ title: "更多在地推薦・More Local Picks", links: remaining });
  }

  return sections;
}
